import React, { useCallback, useEffect, useState } from 'react';
import { authenticationSelector } from '~/components/authentication/ducks/selectors';
import useTranslations from '~/components/translation/use-translations';
import BinaryQuestion from '~/components/binary-question';
import { APP_EVENTS, registerAppEventCallback, sendAppBridgeEvent, WEB_EVENTS } from '~/components/app-bridge';
import {
	acknowledgeRCNotification,
	getNextRCNotification,
	getRealityCheckNotificationInfo,
	startRC,
	viewRCNotification
} from '../../utils/api';
import {
	ButtonProps,
	RealityCheckTexts,
	RealityCheckNotificationInfoResponse,
	BaseRealityCheckState,
	RealityCheckNotification
} from '~/components/account/reality-check-new/utils/types';
import reloadPage from '~/components/account/reality-check/utils';
import RCPopup from '~/components/account/reality-check-new/ui/popup';
import usePrevious from '~/hooks/use-previous';
import shouldRedirectAfterLogout from '~/components/account/should-redirect-after-logout';
import { ROUTE_HOME } from '~/components/router/constants';
import { signout } from '~/components/authentication/ducks';
import { useCloseCurrentContext } from '~/hooks/use-close-context';
import ConfirmMessage, { IconIdentifier } from '~/components/action-screen-confirm-message';
import {
	setUpdateDuration,
	selectUpdateDurationSelector,
	selectTimerId,
	setActiveRCTimerId,
	selectOngoingGameRoundSelector,
	setRealityCheckTimeReached
} from '~/components/account/reality-check-new/store/reality-check-duck';
import { useAppDispatch, useAppSelector } from '~/store/types';

type ConfirmButtonsProps = {
	class?: string;
	name?: string;
	onclick?: () => void;
	dataAt?: string;
	text?: string;
};

const textKeys = [
	'MOBILE_REALITY_CHECK_HEADER',
	'MOBILE_REALITY_CHECK_TIME_PLAYED_HEADER',
	'MOBILE_REALITY_CHECK_BET_SUM_HEADER',
	'MOBILE_REALITY_CHECK_CONTINUE_BUTTON',
	'MOBILE_REALITY_CHECK_BUTTON_LOGOUT',
	'MOBILE_REALITY_CHECK_RESET_TITLE',
	'MOBILE_REALITY_CHECK_RESET_MESSAGE',
	'MOBILE_REALITY_CHECK_RESET_BUTTON',
	'API_ERROR_TITLE',
	'API_ERROR_DESCRIPTION',
	'BUTTON_OK'
];

const initialState: BaseRealityCheckState = {
	show: false,
	notificationId: null,
	nextNotificationId: null,
	nextAction: null,
	error: null,
	popupInfoTexts: undefined,
	apiError: false
};

const RealityCheckSweden: React.FC = () => {
	const [state, setState] = useState<BaseRealityCheckState>(initialState);
	const [popupInfoResponse, setPopupInfo] = useState<RealityCheckNotificationInfoResponse>();
	const [apiError, setApiError] = useState(false);
	const timerId = useAppSelector(appState => selectTimerId(appState as never));
	const durationUpdateRestartRC = useAppSelector(appState => selectUpdateDurationSelector(appState as never));
	const authenticated = useAppSelector(authenticationSelector);
	const ongoingGameRound = useAppSelector(appState => selectOngoingGameRoundSelector(appState as never));
	const prevAuthenticated = usePrevious(authenticated);

	const [confirm, setConfirm] = useState<{
		show: boolean;
		iconIdentifier: string;
		title: string;
		message: string;
		buttonText: string;
		buttonCallback: (() => void) | undefined;
		closeButtonText: string;
		closeCallback: (() => void) | undefined;
		buttons: ConfirmButtonsProps[];
	}>({
		show: false,
		iconIdentifier: IconIdentifier.DEFAULT_SUCCESS,
		title: '',
		message: '',
		buttonText: '',
		buttonCallback: undefined,
		closeButtonText: '',
		closeCallback: undefined,
		buttons: []
	});

	const dispatch = useAppDispatch();
	const closeCurrentContext = useCloseCurrentContext();

	const { translations, isLoadingTranslations } = useTranslations(textKeys);

	const resetRealityCheckState = useCallback(() => {
		if (timerId) {
			clearTimeout(timerId);
		}
		setState(initialState);
		dispatch(setActiveRCTimerId(undefined));
		dispatch(setRealityCheckTimeReached(false));
	}, [dispatch, timerId]);

	const onLogout = async () => {
		// setShow(false);
		setState(prev => ({
			...prev,
			show: false
		}));
		// TODO Call to set the acknowledgement timestamp???
		if (shouldRedirectAfterLogout()) {
			closeCurrentContext({ toLocation: ROUTE_HOME });
		} else {
			closeCurrentContext();
		}
		// eslint-disable-next-line @typescript-eslint/no-floating-promises
		dispatch(signout({}, { logoutType: 'account' }));
		resetRealityCheckState();
	};

	const getViewActionSuccess = (notification: RealityCheckNotification) => {
		const viewAction = notification?.actions?.find(action => action.type === 'VIEW');
		return viewAction?.successful ?? false;
	};

	const displayPopup = useCallback(
		async (id: number, nextAction: string) => {
			dispatch(setRealityCheckTimeReached(true));
			dispatch(setActiveRCTimerId(undefined));
			if (nextAction === 'ACKNOWLEDGEMENT') {
				const notificationInfo = await getRealityCheckNotificationInfo(id);
				if (notificationInfo?.texts) {
					setPopupInfo(
						notificationInfo
							? { ...notificationInfo, texts: notificationInfo.texts as RealityCheckTexts }
							: undefined
					);
					setState(prev => ({
						...prev,
						show: true
					}));
					return;
				}
			}
			if (nextAction === 'VIEW') {
				const notificationInfo = await getRealityCheckNotificationInfo(id);
				if (notificationInfo?.texts) {
					setPopupInfo(
						notificationInfo
							? { ...notificationInfo, texts: notificationInfo.texts as RealityCheckTexts }
							: undefined
					);
				}

				const response = await viewRCNotification(id);
				const viewActionState = getViewActionSuccess(response.notification);
				if (viewActionState) {
					setState(prev => ({
						...prev,
						nextAction: 'ACKNOWLEDGEMENT',
						show: true
					}));
				}
			}
		},
		[dispatch]
	);

	const startRealityCheck = useCallback(async () => {
		try {
			dispatch(setRealityCheckTimeReached(false));
			const response = await startRC();
			if ('notification' in response) {
				const { notification } = response;
				setState(prev => ({
					...prev,
					notificationId: notification.id,
					nextAction: notification.nextAction
				}));

				const oldTimerId = timerId;
				const newTimerId = setTimeout(
					() => void displayPopup(notification.id, notification.nextAction),
					notification.timeLeftInMillis
				);
				if (oldTimerId) {
					clearTimeout(oldTimerId);
				}

				dispatch(setActiveRCTimerId(newTimerId));
				dispatch(setUpdateDuration(false)); // Reset the update duration flag
			}
		} catch (error) {
			setApiError(true);
		}
	}, [dispatch, displayPopup, timerId]);

	const checkOngoingRC = useCallback(async () => {
		if (authenticated) {
			try {
				dispatch(setRealityCheckTimeReached(false));
				const response = await getNextRCNotification();
				if ('notification' in response) {
					const { notification } = response;
					if (notification.id) {
						setState(prev => ({
							...prev,
							notificationId: notification.id,
							nextAction: notification.nextAction
						}));
						const oldTimerId = timerId;
						const newTimerId = setTimeout(
							() => void displayPopup(notification.id, notification.nextAction),
							notification.timeLeftInMillis
						);
						if (oldTimerId) {
							clearTimeout(oldTimerId);
						}
						dispatch(setActiveRCTimerId(newTimerId));
					}
				}
			} catch (error) {
				setApiError(true);
			}
		}
	}, [authenticated, dispatch, displayPopup, timerId]);

	const onContinue = useCallback(async () => {
		if (state.notificationId) {
			try {
				if (state.nextAction === 'ACKNOWLEDGEMENT') {
					// call acknowledge API only if required
					await acknowledgeRCNotification(state.notificationId);
					setState(prev => ({
						...prev,
						show: false
					}));
					setConfirm({
						show: true,
						iconIdentifier: IconIdentifier.DEFAULT_SUCCESS,
						title: translations.MOBILE_REALITY_CHECK_RESET_TITLE,
						message: translations.MOBILE_REALITY_CHECK_RESET_MESSAGE,
						buttonText: translations.MOBILE_REALITY_CHECK_RESET_BUTTON,
						buttons: [
							{
								class: '',
								name: 'close',
								onclick: () => {
									setConfirm(confirmData => ({
										...confirmData,
										show: false
									}));
									// call next API to start the next reality check
									void checkOngoingRC();
								},
								dataAt: 'reset-button-reality-check',
								text: translations.MOBILE_REALITY_CHECK_RESET_BUTTON
							}
						],
						closeButtonText: '',
						closeCallback: undefined,
						buttonCallback: undefined
					});
				}
			} catch (error) {
				setApiError(true);
			}
		}
	}, [
		state.notificationId,
		state.nextAction,
		translations.MOBILE_REALITY_CHECK_RESET_TITLE,
		translations.MOBILE_REALITY_CHECK_RESET_MESSAGE,
		translations.MOBILE_REALITY_CHECK_RESET_BUTTON,
		checkOngoingRC
	]);

	const buttonConfig: ButtonProps[] = [
		{
			onClick: () => void onContinue(),
			dataAt: 'continue-button-reality-check',
			text: translations.MOBILE_REALITY_CHECK_CONTINUE_BUTTON
		},
		{
			onClick: () => void onLogout(),
			dataAt: 'logout-button-reality-check',
			text: translations.MOBILE_REALITY_CHECK_BUTTON_LOGOUT,
			className: 'btn--responsible-gaming-logout'
		}
	];

	useEffect(() => {
		registerAppEventCallback(APP_EVENTS.START_REALITY_CHECK, startRealityCheck);
	}, [startRealityCheck]);

	useEffect(() => {
		if (durationUpdateRestartRC) {
			if (authenticated) {
				void startRealityCheck();
			}
		}
	}, [authenticated, durationUpdateRestartRC, startRealityCheck]);

	useEffect(() => {
		if (authenticated && timerId) return;
		if (typeof prevAuthenticated !== 'undefined' && prevAuthenticated !== authenticated) {
			if (authenticated) {
				void startRealityCheck();
			} else {
				resetRealityCheckState();
			}
		}
	}, [authenticated, prevAuthenticated, resetRealityCheckState, startRealityCheck, timerId]);

	useEffect(() => {
		if (!authenticated) return;
		void checkOngoingRC();
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		sendAppBridgeEvent(WEB_EVENTS.REALITY_CHECK_POPUP, [{ show: state.show }]);
	}, [state.show]);

	if (isLoadingTranslations) {
		return null; // or a loading spinner
	}

	if (confirm.show) {
		const { iconIdentifier, title, message, buttons } = confirm;
		return (
			<ConfirmMessage
				iconIdentifier={iconIdentifier}
				titleText={title}
				messageText={message}
				buttons={buttons}
				fromRealityCheck
				labels={undefined}
				closeButton={undefined}
			/>
		);
	}

	if (!state.show || !popupInfoResponse || ongoingGameRound) {
		return null;
	}

	return apiError ? (
		<BinaryQuestion
			shouldOpenBinary
			title={translations.API_ERROR_TITLE}
			text={translations.API_ERROR_DESCRIPTION}
			onYesClick={reloadPage}
			yesText={translations.BUTTON_OK}
			fixedPosition
		/>
	) : (
		<RCPopup infoResponse={popupInfoResponse} buttonConfig={buttonConfig} />
	);
};

export default RealityCheckSweden;
